import {ComponentType, MouseEvent, useMemo, useState} from 'react'
import {Button} from '@/components/ui/button/Button'
import {DialogDisclosure, DialogDismiss, DialogHeading, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {
    StyledEventFormDialog,
    StyledEventFormDialogBody,
    StyledEventFormDialogHeader
} from '@/features/agenda/components/create-event-form-dialog/style'
import {useTranslation} from '@/translations/i18n'
import {AgendaEventFormFields} from '@/features/agenda/components/agenda-event-form/agenda-event-form-fields/AgendaEventFormFields'
import {AgendaCreateEventFormProvider} from '@/features/agenda/components/agenda-event-form/agenda-create-event-form-provider/AgendaCreateEventFormProvider'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {CreateEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {DialogStore, useDialogStore} from '@ariakit/react'
import {CreateEventFormDialogFooter} from '@/features/agenda/components/create-event-form-dialog/CreateEventFormDialogFooter'
import {AgendaEventStatus} from '@/features/agenda/types'

export const CreateEventFormDialog = ({
    customTrigger: CustomTrigger,
    providedDialogStore,
    defaultValues,
    disabledStatuses,
    isExternalStructure
}: {
    customTrigger?: ComponentType<{onClick: (event?: MouseEvent<HTMLElement>) => void}>
    providedDialogStore?: DialogStore
    defaultValues?: Partial<CreateEventFormValidationSchema>
    disabledStatuses?: AgendaEventStatus[]
    /** Renders the reduced form used to book an event on an external structure */
    isExternalStructure?: boolean
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    // If a dialog store is provided, use it, otherwise use the local dialog store
    const activeDialogStore = useMemo(() => providedDialogStore || dialogStore, [providedDialogStore, dialogStore])

    return (
        <DialogProvider store={activeDialogStore}>
            {!providedDialogStore && (
                <DialogDisclosure
                    render={
                        CustomTrigger ? (
                            <CustomTrigger onClick={() => setIsOpen(true)} />
                        ) : (
                            <Button variant="primaryNeutral">
                                <PlusIcon />
                                {t('agenda:event')}
                            </Button>
                        )
                    }
                />
            )}
            <StyledEventFormDialog width="780px" overflowY="unset">
                <CreateEventFormDialogContent
                    defaultValues={defaultValues}
                    disabledStatuses={disabledStatuses}
                    isExternalStructure={isExternalStructure}
                />
            </StyledEventFormDialog>
        </DialogProvider>
    )
}

const CreateEventFormDialogContent = ({
    defaultValues,
    disabledStatuses,
    isExternalStructure
}: {
    defaultValues?: Partial<CreateEventFormValidationSchema>
    disabledStatuses?: AgendaEventStatus[]
    isExternalStructure?: boolean
}) => {
    const {t} = useTranslation()
    return (
        <>
            <StyledEventFormDialogHeader>
                <DialogHeading>
                    {isExternalStructure ? t('agenda:create_external_structure_event') : t('agenda:create_event')}
                </DialogHeading>
                <DialogDismiss />
            </StyledEventFormDialogHeader>
            <AgendaCreateEventFormProvider defaultValues={defaultValues}>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
                    <StyledEventFormDialogBody>
                        <AgendaEventFormFields mode="create" isExternalStructure={isExternalStructure} />
                    </StyledEventFormDialogBody>
                </ScrollArea>
                <CreateEventFormDialogFooter
                    disabledStatuses={disabledStatuses}
                    isExternalStructure={isExternalStructure}
                />
            </AgendaCreateEventFormProvider>
        </>
    )
}
